// src/pages/Client/MyParcels.jsx
import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Spinner, Alert, Form, Row, Col, Button, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from '../../api/axios';

const MyParcels = () => {
    const [parcels, setParcels] = useState([]);
    const [filteredParcels, setFilteredParcels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchParcels();
    }, []);

    const fetchParcels = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/parcels/');

            let parcelsData = [];
            if (response.data && response.data.results) {
                parcelsData = response.data.results;
            } else if (Array.isArray(response.data)) {
                parcelsData = response.data;
            }

            setParcels(parcelsData);
            setFilteredParcels(parcelsData);
        } catch (err) {
            console.error('Erreur chargement:', err);
            setError('Impossible de charger vos colis');
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let filtered = [...parcels];

        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(p => p.current_status === statusFilter);
        }

        setFilteredParcels(filtered);
        setCurrentPage(1);
    }, [searchTerm, statusFilter, parcels]);

    const getStatusBadge = (status) => {
        const config = {
            registered: { text: '📝 Enregistré', variant: 'secondary' },
            transit_air: { text: '✈️ En transit (aérien)', variant: 'primary' },
            transit_sea: { text: '🚢 En transit (maritime)', variant: 'primary' },
            arrived: { text: '📦 Arrivé', variant: 'warning' },
            delivering: { text: '🚚 En livraison', variant: 'info' },
            delivered: { text: '✅ Livré', variant: 'success' },
            incident: { text: '⚠️ Incident', variant: 'danger' }
        };
        return config[status] || { text: status, variant: 'secondary' };
    };

    const statusOptions = [
        { value: '', label: 'Tous les statuts' },
        { value: 'registered', label: '📝 Enregistré' },
        { value: 'transit_air', label: '✈️ En transit (aérien)' },
        { value: 'transit_sea', label: '🚢 En transit (maritime)' },
        { value: 'arrived', label: '📦 Arrivé' },
        { value: 'delivering', label: '🚚 En livraison' },
        { value: 'delivered', label: '✅ Livré' },
        { value: 'incident', label: '⚠️ Incident' }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredParcels.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredParcels.length / itemsPerPage);

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" style={{ color: '#C62828' }} />
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="border-0 shadow-lg rounded-4">
                <Card.Header className="bg-white border-0 pt-4">
                    <h2 className="fw-bold mb-0" style={{ color: '#C62828' }}>
                        📦 Mes colis
                    </h2>
                    <p className="text-muted mt-2">
                        Suivez l'ensemble de vos colis en temps réel
                    </p>
                </Card.Header>
                <Card.Body className="p-4">
                    {/* Filtres */}
                    <Row className="mb-4">
                        <Col md={6} className="mb-3 mb-md-0">
                            <Form.Control
                                type="text"
                                placeholder="🔍 Rechercher par numéro de suivi ou description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Col>
                        <Col md={4}>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button
                                variant="outline-secondary"
                                onClick={() => { setSearchTerm(''); setStatusFilter(''); }}
                                className="w-100"
                            >
                                Réinitialiser
                            </Button>
                        </Col>
                    </Row>

                    {/* Tableau des colis */}
                    {filteredParcels.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">Aucun colis trouvé</p>
                            <Link to="/tracking" className="btn btn-danger btn-sm">
                                Suivre un colis
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="p-3">N° Suivi</th>
                                            <th className="p-3">Description</th>
                                            <th className="p-3">Statut</th>
                                            <th className="p-3">Date d'envoi</th>
                                            <th className="p-3">Livraison prévue</th>
                                            <th className="p-3">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((parcel) => {
                                            const status = getStatusBadge(parcel.current_status);
                                            const isDelayed = parcel.expected_delivery_date &&
                                                new Date(parcel.expected_delivery_date) < new Date() &&
                                                parcel.current_status !== 'delivered';

                                            return (
                                                <tr key={parcel.id}>
                                                    <td className="p-3 font-monospace fw-bold">
                                                        {parcel.tracking_number}
                                                    </td>
                                                    <td className="p-3">
                                                        {parcel.description?.substring(0, 60) || '-'}
                                                        {parcel.description?.length > 60 && '...'}
                                                    </td>
                                                    <td className="p-3">
                                                        <Badge bg={status.variant} className="px-3 py-2">
                                                            {status.text}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-3">{formatDate(parcel.created_at)}</td>
                                                    <td className="p-3">
                                                        {parcel.expected_delivery_date ? (
                                                            <span className={isDelayed ? 'text-danger fw-bold' : 'text-success'}>
                                                                {formatDate(parcel.expected_delivery_date)}
                                                                {isDelayed && ' ⚠️'}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="p-3">
                                                        <Link
                                                            to={`/tracking/${parcel.tracking_number}`}
                                                            className="btn btn-sm btn-outline-danger"
                                                            target="_blank"
                                                        >
                                                            📍 Suivre
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />

                                        {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = idx + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = idx + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + idx;
                                            } else {
                                                pageNum = currentPage - 2 + idx;
                                            }

                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={pageNum === currentPage}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}

                                        <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}

                            {/* Information */}
                            <div className="text-center text-muted small mt-3">
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredParcels.length)} sur {filteredParcels.length} colis
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default MyParcels;